import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight } from 'lucide-react';

const API_URL = window.__BACKEND_URL;

export default function PublicPricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/pricing-plans`);
      setPlans(response.data);
    } catch (err) {
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-xl">
              C
            </div>
            <span className="text-2xl font-bold text-white">Consistency</span>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/help')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              Help
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 px-6 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Choose the plan that fits your content creation needs
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan, idx) => {
              const isPopular = plan.display_name === 'Pro' || plan.display_name === 'Creator';
              
              return (
                <div
                  key={plan.id}
                  className={`relative p-8 rounded-2xl ${
                    isPopular
                      ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500'
                      : 'bg-white/5 border border-white/10'
                  } backdrop-blur-md`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-bold rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {plan.display_name}
                    </h3>
                    <div className="text-5xl font-bold text-white mb-2">
                      ${plan.monthly_price}
                      <span className="text-xl text-gray-400">/mo</span>
                    </div>
                    {plan.annual_price && (
                      <p className="text-sm text-gray-400">
                        or ${plan.annual_price}/year (save 20%)
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => navigate('/signup')}
                    className={`w-full py-3 rounded-lg font-semibold mb-8 transition-all ${
                      isPopular
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Get Started
                  </button>

                  <div className="space-y-4">
                    {plan.features_json && typeof plan.features_json === 'object' && (
                      Object.entries(plan.features_json).map(([feature, included]) => (
                        <div key={feature} className="flex items-start gap-3">
                          {included ? (
                            <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${
                            included ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ or CTA */}
      <section className="py-20 px-6 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join creators who are transforming their content workflow
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xl rounded-lg hover:opacity-90 transition-opacity font-bold inline-flex items-center gap-3"
          >
            Start Free Trial
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-gray-400 text-sm">
          © 2025 Consistency. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
